import React, { useState, useCallback, useRef } from 'react';
import Papa from 'papaparse';
import { Icons } from '../ui/Icons';
import { ParkingReservation, ReservationSource } from '../../types';
import toast from 'react-hot-toast';

interface CSVImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImport: (data: ParkingReservation[]) => void;
    existingReservations: ParkingReservation[];
    reservationSources: ReservationSource[];
}

interface ValidationResult {
    row: any;
    isValid: boolean;
    isDuplicate: boolean;
    existingReservation?: ParkingReservation;
    errors: string[];
    transformedData?: ParkingReservation;
    mappedValues?: {
        reservationNum: string;
        guestName: string;
        plate: string;
        source: string;
    };
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({ isOpen, onClose, onImport, existingReservations, reservationSources }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [results, setResults] = useState<ValidationResult[]>([]);
    const [summary, setSummary] = useState({ total: 0, valid: 0, invalid: 0, duplicates: 0 });
    const [importMode, setImportMode] = useState<'file' | 'paste'>('file');
    const [importFormat, setImportFormat] = useState<'one-stop' | 'park-fly'>('one-stop');
    const [pasteData, setPasteData] = useState('');
    const [selectedOverrides, setSelectedOverrides] = useState<Set<string>>(new Set());
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ─────────────────────────────────────────────────────────────────────────
    // HELPERS
    // ─────────────────────────────────────────────────────────────────────────

    /** Build a normalised lookup map from a raw CSV row object. */
    const buildNormalizedRow = (row: any): Record<string, string> => {
        const normalized: Record<string, string> = {};
        Object.keys(row).forEach(key => {
            const cleanKey = key.toString().replace(/^\uFEFF/, '').trim().toLowerCase();
            const noSpecial = cleanKey.replace(/[^a-z0-9]/g, '');
            const value = row[key] != null ? row[key].toString().trim() : '';
            normalized[cleanKey] = value;
            normalized[noSpecial] = value;
        });
        return normalized;
    };

    /** Return the first non-empty value whose key matches any of the candidates. */
    const getValue = (normalizedRow: Record<string, string>, possibleKeys: string[]): string => {
        for (const key of possibleKeys) {
            const lowerKey = key.toLowerCase();
            const cleanKey = lowerKey.replace(/[^a-z0-9]/g, '');
            if (normalizedRow[lowerKey]) return normalizedRow[lowerKey];
            if (normalizedRow[cleanKey]) return normalizedRow[cleanKey];
        }
        return '';
    };

    /** Format a Date object to YYYY-MM-DD using local time (no timezone shift). */
    const formatDate = (date: Date): string => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    /** Parse dates in DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, or ISO formats. */
    const parseDate = (dateStr: string): Date | null => {
        if (!dateStr) return null;
        const s = dateStr.trim();

        // Try native parsing (handles ISO 8601 and many locale formats)
        const native = new Date(s);
        if (!isNaN(native.getTime())) return native;

        // Manual multi-format fallback
        const parts = s.split(/[^0-9]/).filter(p => p.length > 0);
        if (parts.length >= 3) {
            const p0 = parseInt(parts[0], 10);
            const p1 = parseInt(parts[1], 10);
            const p2 = parseInt(parts[2], 10);

            // YYYY-MM-DD
            if (parts[0].length === 4) return new Date(p0, p1 - 1, p2);
            // DD/MM/YYYY (if day > 12)
            if (p0 > 12) return new Date(p2 < 100 ? 2000 + p2 : p2, p1 - 1, p0);
            // MM/DD/YYYY (if month part > 12)
            if (p1 > 12) return new Date(p2 < 100 ? 2000 + p2 : p2, p0 - 1, p1);
            // Default: MM/DD/YYYY
            return new Date(p2 < 100 ? 2000 + p2 : p2, p0 - 1, p1);
        }
        return null;
    };

    /** Title-case a string ("john doe" → "John Doe"). */
    const capitalize = (str: string): string =>
        str ? str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '';

    /** Remove spaces and uppercase a plate number. */
    const cleanPlate = (plate: string): string =>
        plate ? plate.replace(/\s+/g, '').toUpperCase() : '-';

    // ─────────────────────────────────────────────────────────────────────────
    // PARK & FLY – PARKING TYPE NORMALIZATION
    //
    //  Rules (spec):
    //  • Remove "PARK & FLY" / "PARK N FLY" / "PARK AND FLY"
    //  • WK / WEEK / WEEKS → "Week" (singular) or "Weeks" (plural)
    //  • DAY / DAYS → "Day" (singular) or "Days" (plural)
    //  • Keep "Outdoor" if present
    //  • Always append "Parking" at the end
    //  • Output: "{N} {Week|Weeks|Day|Days} [Outdoor] Parking"
    //
    //  Examples:
    //  "PARK N FLY 1 WEEK"               → "1 Week Parking"
    //  "PARK & FLY 10 days OUTDOOR"      → "10 Days Outdoor Parking"
    //  "PARK & FLY 2 WK OUTDOOR"         → "2 Weeks Outdoor Parking"
    //  "PARK & FLY 3 WEEKS OUTDOOR"      → "3 Weeks Outdoor Parking"
    // ─────────────────────────────────────────────────────────────────────────
    const normalizeParkingType = (raw: string): string => {
        if (!raw) return '';

        let s = raw.trim().toUpperCase();

        // Remove brand prefixes
        s = s.replace(/PARK\s*[&N]\s*(AND\s*)?FLY/gi, '').trim();
        s = s.replace(/PARK\s+AND\s+FLY/gi, '').trim();

        // Detect number + unit
        const match = s.match(/(\d+)\s*(WEEKS?|WKS?|DAYS?)/i);
        let durationLabel = '';
        let hasOutdoor = /OUTDOOR/i.test(s);

        if (match) {
            const num = parseInt(match[1], 10);
            const unit = match[2].toUpperCase();

            if (unit.startsWith('W')) {
                // Week(s)
                durationLabel = `${num} ${num === 1 ? 'Week' : 'Weeks'}`;
            } else {
                // Day(s)
                durationLabel = `${num} ${num === 1 ? 'Day' : 'Days'}`;
            }
        }

        // Build output: "{duration} [Outdoor] Parking"
        const parts: string[] = [];
        if (durationLabel) parts.push(durationLabel);
        if (hasOutdoor) parts.push('Outdoor');
        parts.push('Parking');

        return parts.join(' ');
    };

    // ─────────────────────────────────────────────────────────────────────────
    // PARK & FLY – DURATION EXTRACTION (days)
    //
    //  Reads the raw parking type string and returns a number of days.
    //  "1 WEEK" → 7, "2 WK" → 14, "10 days" → 10
    //  Missing / unrecognised → 0 (caller adds the Nights default)
    // ─────────────────────────────────────────────────────────────────────────
    const extractDurationDays = (raw: string): number => {
        if (!raw) return 0;
        const s = raw.toUpperCase();

        const weekMatch = s.match(/(\d+)\s*(WEEKS?|WKS?)/i);
        if (weekMatch) return parseInt(weekMatch[1], 10) * 7;

        const dayMatch = s.match(/(\d+)\s*DAYS?/i);
        if (dayMatch) return parseInt(dayMatch[1], 10);

        return 0;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // MATCH SOURCE NAME
    // ─────────────────────────────────────────────────────────────────────────
    const matchSource = (sourceValue: string, format: 'one-stop' | 'park-fly'): string => {
        const defaultSource = format === 'park-fly' ? 'Park & Fly' : 'One Stop Parking';
        if (!sourceValue || reservationSources.length === 0) return defaultSource;

        const normalizedInput = sourceValue.toLowerCase().replace(/[^a-z0-9]/g, '');
        const exact = reservationSources.find(s =>
            s.name.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedInput
        );
        if (exact) return exact.name;

        const partial = reservationSources.find(s => {
            const n = s.name.toLowerCase().replace(/[^a-z0-9]/g, '');
            return normalizedInput.length > 2 && (normalizedInput.includes(n) || n.includes(normalizedInput));
        });
        return partial ? partial.name : defaultSource;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // ROW VALIDATION & TRANSFORMATION
    // ─────────────────────────────────────────────────────────────────────────
    const validateRow = (row: any): ValidationResult => {
        const errors: string[] = [];
        const nr = buildNormalizedRow(row);
        const get = (keys: string[]) => getValue(nr, keys);

        // ── Shared variable declarations ──────────────────────────────────────
        let reservationNum = '';
        let fromValue = '';
        let guestValue = '';
        let plateValue = '';
        let roomValue = '';
        let endTimeValue = '';
        let statusValue = '';
        let peopleValue = '';
        let authValue = '';
        let checkInValue = '';
        let checkOutValue = '';
        let roomTypeValue = '';
        let sleepDatesValue = '';
        let parkingCountValue = '';
        let roomCountValue = '';
        let notesValue = '';
        let dueAtLocationValue = '';
        let sourceValue = '';
        let phoneValue = '';
        let emailValue = '';
        let totalValue = '';
        let carValue = '';

        // ══════════════════════════════════════════════════════════════════════
        // ONE STOP PARKING – field mapping (unchanged from original)
        // ══════════════════════════════════════════════════════════════════════
        if (importFormat === 'one-stop') {
            reservationNum = get([
                'Reservation #', 'res #', 'confirmation #', 'conf #',
                'confirmation_number', 'confirmationnumber', 'res_no', 'res no',
                'conf_no', 'conf no', 'booking_id', 'booking id', 'booking_no',
                'booking no', 'res. #', 'account'
            ]);
            fromValue = get([
                'From', 'datetime', 'start date', 'start_date', 'startdate',
                'arrival', 'check-in', 'checkin', 'arrival_date', 'arrival date',
                'start_time', 'start time'
            ]);
            guestValue = get([
                'Guest', 'guest name', 'customer_name', 'customer', 'name',
                'guestname', 'guest_name', 'full name', 'fullname', 'client',
                'client_name', 'customer name'
            ]);
            plateValue = get([
                'vehicle_plate', 'plate', 'license plate', 'vehicle plate',
                'license_plate', 'license plate #', 'plate #', 'vehicle plate #',
                'license', 'car plate', 'car plate #', 'registration',
                'registration #', 'reg #', 'l-plate', 'l plate', 'v-plate',
                'v plate', 'veh plate', 'lic plate', 'plate_number', 'plate number'
            ]);
            roomValue = get(['room', 'room number', 'room #', 'room_number', 'room_no', 'room no', 'roomno', 'unit', 'unit_number']);
            endTimeValue = get(['end_time', 'end date', 'to', 'until', 'checkout date', 'end_date', 'departure', 'departure_date', 'departure date', 'check-out', 'return date']);
            statusValue = get(['status', 'reservation status', 'res_status', 'booking_status']);
            peopleValue = get(['people', 'guests', 'pax', 'number of people', 'number_of_people', 'guest_count', 'occupancy', '# of people']);
            authValue = get(['authorized by', 'auth by', 'authorized_by', 'authorizedBy', 'auth_by', 'approver', 'approved_by']);
            checkInValue = get(['hotel check-in', 'check-in date', 'hotel_checkin', 'hotelCheckInDate', 'hotel_arrival', 'hotel arrival']);
            checkOutValue = get(['hotel check-out', 'check-out date', 'hotel_checkout', 'hotelCheckOutDate', 'hotel_departure', 'hotel departure']);
            roomTypeValue = get(['room type', 'room_type', 'roomType', 'type of room', 'room_category', 'room category', 'unit_type', 'unit type', 'parking type', 'park & fly', 'source']);
            sleepDatesValue = get(['dates of sleep', 'nights', 'datesOfSleep', 'sleep dates', 'stay dates', 'duration', 'length_of_stay']);
            parkingCountValue = get(['number of parkings', 'parkings', 'parking count', 'numberOfParkings', '# of parkings', 'parkings #', 'no of parkings', 'no. of parkings', 'parking_count', 'parking_qty', 'parking qty', 'parking_spaces', 'parking spaces']);
            roomCountValue = get(['number of rooms', 'rooms', 'room count', 'numberOfRooms', '# of rooms', 'rooms #', 'no of rooms', 'no. of rooms', 'room_count', 'room_qty', 'room qty', 'units', 'number_of_units']);
            notesValue = get(['notes', 'comments', 'remarks', 'description', 'memo', 'special_requests']);
            dueAtLocationValue = get(['due at location', 'due_at_location', 'dueAtLocation', 'due', 'balance']);
            sourceValue = get(['source', 'reservation source', 'reservationSource', 'channel', 'reservation channel', 'res channel', 'booking channel', 'market_segment', 'market segment']);
            phoneValue = get(['customer phone', 'phone', 'telephone', 'mobile', 'cell', 'phone number']);
            emailValue = get(['email', 'e-mail', 'customer email', 'guest email', 'mail']);
            totalValue = get(['total', 'amount', 'price', 'cost']);
            carValue = get(['car make/model', 'car', 'vehicle', 'make', 'model', 'car_make_model']);
        }

        // ══════════════════════════════════════════════════════════════════════
        // PARK & FLY – field mapping (FIXED per spec)
        // ══════════════════════════════════════════════════════════════════════
        else if (importFormat === 'park-fly') {

            // ── Required fields ───────────────────────────────────────────────
            // CSV: "Client Name" → Customer name
            guestValue = get([
                'Client Name', 'Customer name', 'Guest Name', 'Guest', 'Name', 'Full Name'
            ]);

            // CSV: "Reservation #" / "Account" → reservation number
            reservationNum = get([
                'Account', 'Reservation #', 'Res #', 'Confirmation #',
                'Conf #', 'Booking ID', 'Reservation No'
            ]);

            // CSV: "Check-in Date" → Check-in (fromValue)
            fromValue = get([
                'Check-in Date', 'Check-in', 'Checkin', 'Check in Date',
                'Check in', 'Arrival', 'Arrival Date', 'From', 'Start Date'
            ]);

            // ── Optional / derived fields ─────────────────────────────────────
            // CSV: "Return Date" → Check-out (endTimeValue) – may be missing; will be calculated
            endTimeValue = get([
                'Return Date', 'Check-out', 'Checkout', 'Check out Date',
                'Check out', 'To', 'End Date'
            ]);

            // CSV: "Arrival" → hotelCheckInDate
            checkInValue = get(['Arrival', 'Arrival Date', 'Hotel Arrival', 'Hotel Check-in', 'Hotel Checkin']);

            // CSV: "Departure" → hotelCheckOutDate
            checkOutValue = get(['Departure', 'Departure Date', 'Hotel Departure', 'Hotel Check-out', 'Hotel Checkout']);

            // CSV: "Parking Type" / "Park & Fly" (column) → parkingType / roomTypeValue
            const rawParkingType = get([
                'Park & Fly', 'Parking Type', 'Company', 'Source',
                'Park And Fly', 'ParkFly', 'Type'
            ]);

            // CSV: "Phone Number" → Customer Phone
            phoneValue = get([
                'Phone Number', 'Customer Phone', 'Phone', 'Mobile', 'Cell', 'Telephone'
            ]);
            
            // CSV: "Email" → Customer Email
            emailValue = get([
                'Email', 'E-mail', 'Customer Email', 'Guest Email', 'Mail'
            ]);

            // CSV: "License Plate" → License Plate  (default "-")
            plateValue = get([
                'License Plate', 'Plate', 'Vehicle Plate', 'License', 'Plate #',
                'License Plate #', 'vehicle_plate'
            ]) || '-';

            // CSV: "Vehicle" → Car Make/Model  (default "-")
            carValue = get([
                'Vehicle', 'Car Make/Model', 'Car', 'Make', 'Model',
                'Car Make', 'Car Model', 'car_make_model'
            ]) || '-';

            // CSV: "# of People"  (default 1)
            peopleValue = get([
                '# of People', 'Number of People', 'People', 'Guests', 'Pax',
                'Guest Count', 'Occupancy'
            ]) || '1';

            // CSV: "Authorized By"  (default "Reception")
            authValue = get([
                'Authorized By', 'Auth By', 'Authorized_by', 'Approver', 'Approved By'
            ]) || 'Reception';

            // Note: use explicit Note field if present, otherwise fall back to the
            // raw "Park & Fly" / "Company" column value (e.g. "PARK N FLY 1 WEEK"),
            // and finally default to the string "Park & Fly".
            const rawParkingTypeForNotes = get([
                'Park & Fly', 'Parking Type', 'Company', 'Source',
                'Park And Fly', 'ParkFly', 'Type'
            ]);
            notesValue = get(['Note', 'Notes', 'Comments', 'Remarks', 'Memo'])
                || rawParkingTypeForNotes
                || 'Park & Fly';

            // Source is always "Park & Fly" for this format
            sourceValue = 'Park & Fly';

            // ── PARKING TYPE NORMALIZATION ────────────────────────────────────
            //   Apply the spec normalization rules to produce clean label.
            roomTypeValue = normalizeParkingType(rawParkingType);

            // ── RETURN DATE CALCULATION ───────────────────────────────────────
            //
            //   Return Date = Check-in Date + duration (from Parking Type) + Nights
            //
            //   Nights rules (spec):
            //   • If a "Nights" column exists → use that value
            //   • Otherwise → default +1 night
            //
            //   We ALWAYS recalculate when fromValue is present to ensure accuracy.
            //   If endTimeValue was already provided in the CSV we still recalculate
            //   because the CSV "Departure" column usually represents the hotel
            //   checkout, not the parking return date.
            // ─────────────────────────────────────────────────────────────────
            if (fromValue) {
                const startDateObj = parseDate(fromValue);
                if (startDateObj) {
                    // Extract parking duration in days from the raw parking type
                    const parkingDays = extractDurationDays(rawParkingType);

                    // Nights: use explicit "Nights" field if present, else default = 1
                    const nightsRaw = get([
                        'Nights', 'Night', 'Stay Nights', 'Extra Nights', 'Hotel Nights'
                    ]);
                    const nights = nightsRaw !== '' && !isNaN(parseInt(nightsRaw, 10))
                        ? parseInt(nightsRaw, 10)
                        : 1; // ← DEFAULT = 1 night (spec)

                    const totalDays = parkingDays + nights;

                    const returnDate = new Date(startDateObj);
                    returnDate.setDate(returnDate.getDate() + totalDays);
                    endTimeValue = formatDate(returnDate);
                }
            }
        }

        // ── SOURCE MATCHING ───────────────────────────────────────────────────
        const matchedSource = matchSource(sourceValue, importFormat);

        // ── VALIDATION ────────────────────────────────────────────────────────
        if (!reservationNum) errors.push('Missing required field: Reservation #');
        if (!fromValue) errors.push('Missing required field: Check-in');
        if (!guestValue) errors.push('Missing required field: Customer Name');

        // Duplicate check
        const existingReservation = reservationNum ? existingReservations.find(r => r.confirmationNumber === reservationNum) : undefined;
        const isDuplicate = !!existingReservation;

        // Date validation
        const startDateObj = fromValue ? parseDate(fromValue) : null;
        if (fromValue && !startDateObj) {
            errors.push(`Invalid date format: ${fromValue}`);
        }

        const isValid = errors.length === 0;

        const mappedValues = {
            reservationNum,
            guestName: guestValue,
            plate: cleanPlate(plateValue),
            source: matchedSource
        };

        // ── TRANSFORM ─────────────────────────────────────────────────────────
        let transformedData: ParkingReservation | undefined;

        if (isValid && startDateObj) {
            const etaRaw = get(['eta', 'estimated time of arrival', 'arrival time', 'arrival_time', 'time']);
            const timeMatch = fromValue.match(/(\d{1,2}:\d{2})/);
            const extractedTime = etaRaw || (timeMatch ? timeMatch[1] : '');

            const parsedEndDate = parseDate(endTimeValue);
            const parsedCheckIn = parseDate(checkInValue);
            const parsedCheckOut = parseDate(checkOutValue);

            transformedData = {
                id: crypto.randomUUID(),
                confirmationNumber: reservationNum,
                guestName: capitalize(guestValue) || 'Unknown Guest',
                vehicle_plate: cleanPlate(plateValue),
                roomNumber: (roomValue || '0').replace(/^R/i, ''),
                startDate: formatDate(startDateObj),
                endDate: parsedEndDate ? formatDate(parsedEndDate) : formatDate(startDateObj),
                status: 'Reservation',
                reservationSource: matchedSource,
                numberOfPeople: String(Math.max(1, parseInt(peopleValue || '1', 10) || 1)),
                authorizedBy: authValue || 'Reception',
                createdAt: new Date().toISOString(),
                eta: extractedTime,
                hotelCheckInDate: parsedCheckIn ? formatDate(parsedCheckIn) : '',
                hotelCheckOutDate: parsedCheckOut ? formatDate(parsedCheckOut) : '',
                roomType: roomTypeValue || '',
                datesOfSleep: sleepDatesValue || '',
                numberOfParkings: String(Math.max(1, parseInt(parkingCountValue || '1', 10) || 1)),
                numberOfRooms: String(Math.max(1, parseInt(roomCountValue || '1', 10) || 1)),
                notes: notesValue || '',
                dueAtLocation: dueAtLocationValue || '',
                customerPhone: phoneValue || '',
                customerEmail: emailValue || '',
                total: totalValue || '',
                carMakeModel: carValue || '',
            };

            // Status normalisation (CSV override)
            if (statusValue) {
                const statusMap: Record<string, string> = {
                    'pending': 'Reservation', 'reservation': 'Reservation', 'confirmed': 'Reservation',
                    'check-in': 'Check-in', 'checkin': 'Check-in', 'checked-in': 'Check-in', 'active': 'Check-in',
                    'checkout': 'Checkout', 'check-out': 'Checkout', 'checked-out': 'Checkout',
                    'completed': 'Checkout', 'finished': 'Checkout'
                };
                const mapped = statusMap[statusValue.toLowerCase()];
                if (mapped) transformedData.status = mapped as any;
            }
        }

        return { row, isValid, isDuplicate, existingReservation, errors, transformedData, mappedValues };
    };

    // ─────────────────────────────────────────────────────────────────────────
    // PARSE / IMPORT HANDLERS
    // ─────────────────────────────────────────────────────────────────────────
    const runValidation = (rows: any[]) => {
        const validatedRows = rows.map(validateRow);
        const validCount = validatedRows.filter(r => r.isValid && !r.isDuplicate).length;
        const duplicateCount = validatedRows.filter(r => r.isValid && r.isDuplicate).length;
        const invalidCount = validatedRows.filter(r => !r.isValid).length;
        
        setResults(validatedRows);
        setSummary({ total: validatedRows.length, valid: validCount, invalid: invalidCount, duplicates: duplicateCount });
        setIsProcessing(false);
    };

    const parseCSV = (file: File) => {
        setIsProcessing(true);
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (res) => runValidation(res.data),
            error: (err) => { console.error(err); setIsProcessing(false); toast.error('Error parsing CSV file'); }
        });
    };

    const handlePasteImport = () => {
        if (!pasteData.trim()) { toast.error('Please paste some data first'); return; }
        setIsProcessing(true);
        Papa.parse(pasteData.trim(), {
            header: true,
            skipEmptyLines: true,
            delimiter: '',
            complete: (res) => runValidation(res.data),
            error: (err) => { console.error(err); setIsProcessing(false); toast.error('Error parsing pasted data'); }
        });
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) parseCSV(e.target.files[0]);
    };

    const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault(); setIsDragging(false);
        if (e.dataTransfer.files?.[0]) parseCSV(e.dataTransfer.files[0]);
    };

    const handleImport = () => {
        const dataToImport = results
            .filter(r => r.isValid && r.transformedData)
            .map(r => {
                const data = { ...r.transformedData! };
                if (r.isDuplicate && r.existingReservation && selectedOverrides.has(r.existingReservation.confirmationNumber)) {
                    // Use existing ID to trigger an update instead of a new creation
                    data.id = r.existingReservation.id;
                    // Merge data: keep existing fields if new ones are empty/placeholder
                    // This helps "complete missing fields" as requested
                    Object.keys(data).forEach(key => {
                        const k = key as keyof ParkingReservation;
                        const newValue = data[k];
                        const oldValue = r.existingReservation![k];
                        
                        if (!newValue || newValue === '-' || newValue === '.') {
                            if (oldValue) (data as any)[k] = oldValue;
                        }
                    });
                }
                return data;
            })
            .filter(data => {
                // If it's a duplicate but NOT selected for override, skip it
                const isDup = existingReservations.some(r => r.confirmationNumber === data.confirmationNumber);
                if (isDup) {
                    const existing = existingReservations.find(r => r.confirmationNumber === data.confirmationNumber);
                    return selectedOverrides.has(existing!.confirmationNumber);
                }
                return true;
            });

        if (dataToImport.length > 0) {
            onImport(dataToImport);
            toast.success(`Successfully processed ${dataToImport.length} reservations`);
            onClose();
        } else {
            toast.error('No records to import');
        }
    };

    if (!isOpen) return null;

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-950">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-indigo-600 rounded-2xl text-white shadow-lg shadow-indigo-200 dark:shadow-none">
                            <Icons.FileText className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Import Data</h2>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-0.5">Bulk reservation processing</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all text-slate-400 hover:text-slate-600">
                        <Icons.X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-8 bg-white dark:bg-slate-950">
                    {results.length === 0 ? (
                        <div className="space-y-8">
                            {/* Format toggle */}
                            <div className="space-y-4">
                                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest text-center">Select Data Format</p>
                                <div className="flex p-1 bg-slate-100 dark:bg-slate-900 rounded-2xl w-full max-w-md mx-auto">
                                    <button onClick={() => setImportFormat('one-stop')} className={`flex-1 px-6 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${importFormat === 'one-stop' ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>One Stop Parking</button>
                                    <button onClick={() => setImportFormat('park-fly')} className={`flex-1 px-6 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${importFormat === 'park-fly' ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>Park & Fly</button>
                                </div>
                            </div>

                            {/* Mode toggle */}
                            <div className="flex p-1 bg-slate-100 dark:bg-slate-900 rounded-2xl w-fit mx-auto">
                                <button onClick={() => setImportMode('file')} className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${importMode === 'file' ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>File Upload</button>
                                <button onClick={() => setImportMode('paste')} className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${importMode === 'paste' ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>Copy & Paste</button>
                            </div>

                            {importMode === 'file' ? (
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`border-2 border-dashed rounded-[2.5rem] p-16 flex flex-col items-center justify-center gap-6 cursor-pointer transition-all group ${isDragging ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10' : 'border-slate-200 dark:border-slate-800 hover:border-indigo-400 hover:bg-slate-50/50 dark:hover:bg-slate-900/20'}`}
                                >
                                    <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".csv" className="hidden" />
                                    <div className="p-6 bg-indigo-50 dark:bg-indigo-900/30 rounded-3xl text-indigo-600 group-hover:scale-110 transition-transform">
                                        <Icons.Upload className="w-10 h-10" />
                                    </div>
                                    <div className="text-center space-y-2">
                                        <p className="text-xl font-black text-slate-900 dark:text-white">Drop CSV file here</p>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                                            {importFormat === 'park-fly'
                                                ? 'Required: Customer Name, Reservation #, Check-in Date'
                                                : 'Required: Reservation #, From, Guest'}
                                        </p>
                                        <div className="pt-4">
                                            <span className="px-4 py-2 bg-slate-900 dark:bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest rounded-xl">Browse Files</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="relative">
                                        <textarea
                                            value={pasteData}
                                            onChange={(e) => setPasteData(e.target.value)}
                                            placeholder="Paste your data here (Tab or Comma separated)..."
                                            className="w-full h-64 p-6 bg-slate-50 dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 rounded-[2rem] text-xs font-medium focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none dark:text-white"
                                        />
                                        <div className="absolute bottom-4 right-4 flex gap-2">
                                            <button onClick={() => setPasteData('')} className="px-4 py-2 bg-white dark:bg-slate-800 text-[10px] font-black text-rose-600 uppercase tracking-widest rounded-xl shadow-sm hover:bg-rose-50 transition-colors">Clear</button>
                                            <button onClick={handlePasteImport} disabled={!pasteData.trim() || isProcessing} className="px-6 py-2 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-indigo-200 dark:shadow-none hover:bg-indigo-700 transition-all disabled:opacity-50">Process Data</button>
                                        </div>
                                    </div>
                                    <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-100 dark:border-slate-800/50">
                                        <div className="flex justify-between items-center mb-3">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Expected Columns:</p>
                                            {importFormat === 'park-fly' && (
                                                <a
                                                    href="data:text/csv;charset=utf-8,Client Name,Account,Check-in Date,Return Date,Park %26 Fly,Phone Number,License Plate,Vehicle,Nights"
                                                    download="park_fly_template.csv"
                                                    className="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:underline"
                                                >
                                                    Download Template
                                                </a>
                                            )}
                                        </div>
                                        <p className="text-[10px] font-mono text-slate-500 dark:text-slate-400 break-all leading-relaxed">
                                            {importFormat === 'one-stop'
                                                ? 'Customer name • Res. # • Check-in • Check-out • Parking type • Due at location • Customer Phone • Total • License Plate • Car Make/Model'
                                                : 'Client Name • Account • Check-in Date • Return Date • Park & Fly • Phone Number • License Plate • Vehicle • Nights'}
                                        </p>
                                        {importFormat === 'park-fly' && (
                                            <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl">
                                                <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-2">Auto-calculation</p>
                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed">
                                                    Return Date = Check-in + parking duration + nights (default: +1 night).
                                                    Leave "Return Date" blank to auto-calculate from the "Park & Fly" column.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* Summary */}
                            <div className="grid grid-cols-4 gap-4">
                                <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800/50">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total</p>
                                    <p className="text-2xl font-black text-slate-900 dark:text-white">{summary.total}</p>
                                </div>
                                <div className="p-4 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100/50 dark:border-emerald-900/20">
                                    <p className="text-[10px] font-black text-emerald-600/60 dark:text-emerald-400/60 uppercase tracking-widest mb-1">New</p>
                                    <p className="text-2xl font-black text-emerald-700 dark:text-emerald-400">{summary.valid}</p>
                                </div>
                                <div className="p-4 bg-amber-50/50 dark:bg-amber-900/10 rounded-2xl border border-amber-100/50 dark:border-amber-900/20">
                                    <p className="text-[10px] font-black text-amber-600/60 dark:text-amber-400/60 uppercase tracking-widest mb-1">Duplicates</p>
                                    <p className="text-2xl font-black text-amber-700 dark:text-amber-400">{summary.duplicates}</p>
                                </div>
                                <div className="p-4 bg-rose-50/50 dark:bg-rose-900/10 rounded-2xl border border-rose-100/50 dark:border-rose-900/20">
                                    <p className="text-[10px] font-black text-rose-600/60 dark:text-rose-400/60 uppercase tracking-widest mb-1">Invalid</p>
                                    <p className="text-2xl font-black text-rose-700 dark:text-rose-400">{summary.invalid}</p>
                                </div>
                            </div>

                            {/* Results table */}
                            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-50/50 dark:bg-slate-900/50">
                                        <tr>
                                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                <div className="flex items-center gap-2">
                                                    Override
                                                    {results.some(r => r.isDuplicate) && (
                                                        <button
                                                            onClick={() => {
                                                                const allDupConfNums = results
                                                                    .filter(r => r.isDuplicate && r.existingReservation)
                                                                    .map(r => r.existingReservation!.confirmationNumber);
                                                                
                                                                if (selectedOverrides.size === allDupConfNums.length) {
                                                                    setSelectedOverrides(new Set());
                                                                } else {
                                                                    setSelectedOverrides(new Set(allDupConfNums));
                                                                }
                                                            }}
                                                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                                            title="Toggle All Overrides"
                                                        >
                                                            <Icons.CheckSquare className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </th>
                                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Conf #</th>
                                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Guest</th>
                                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Plate</th>
                                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Return Date</th>
                                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Parking Type</th>
                                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Errors</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {results.map((res, idx) => (
                                            <tr key={idx} className={`group hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors ${res.isValid ? '' : 'bg-rose-50/30 dark:bg-rose-900/5'}`}>
                                                <td className="px-6 py-4">
                                                    {res.isValid ? (
                                                        res.isDuplicate ? (
                                                            <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600" title="Duplicate Reservation">
                                                                <Icons.Copy className="w-3.5 h-3.5" />
                                                            </div>
                                                        ) : (
                                                            <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600">
                                                                <Icons.Check className="w-3.5 h-3.5" />
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div className="w-6 h-6 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-rose-600">
                                                            <Icons.AlertTriangle className="w-3.5 h-3.5" />
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {res.isDuplicate && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedOverrides.has(res.existingReservation!.confirmationNumber)}
                                                            onChange={(e) => {
                                                                const newOverrides = new Set(selectedOverrides);
                                                                if (e.target.checked) {
                                                                    newOverrides.add(res.existingReservation!.confirmationNumber);
                                                                } else {
                                                                    newOverrides.delete(res.existingReservation!.confirmationNumber);
                                                                }
                                                                setSelectedOverrides(newOverrides);
                                                            }}
                                                            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                                                        />
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-xs font-bold text-slate-900 dark:text-white">
                                                    {res.transformedData?.confirmationNumber || res.mappedValues?.reservationNum || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-xs font-medium text-slate-600 dark:text-slate-400">
                                                    {res.transformedData?.guestName || res.mappedValues?.guestName || '-'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-black font-mono tracking-widest">
                                                        {res.transformedData?.vehicle_plate || res.mappedValues?.plate || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-xs font-medium text-slate-600 dark:text-slate-400">
                                                    {res.transformedData?.endDate || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-xs font-medium text-slate-600 dark:text-slate-400">
                                                    {res.transformedData?.roomType || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-[10px] text-rose-600 font-bold uppercase tracking-tighter">
                                                    {res.errors.join(', ')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-8 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50">
                    <button
                        onClick={() => { setResults([]); setSummary({ total: 0, valid: 0, invalid: 0, duplicates: 0 }); }}
                        className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-rose-600 transition-colors"
                    >
                        Reset Import
                    </button>
                    <div className="flex gap-4">
                        <button onClick={onClose} className="px-8 py-3 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300 transition-colors">Cancel</button>
                        <button
                            onClick={handleImport}
                            disabled={(summary.valid === 0 && selectedOverrides.size === 0) || isProcessing}
                            className={`px-10 py-3 text-xs font-black uppercase tracking-[0.2em] rounded-2xl shadow-lg transition-all flex items-center gap-3 ${(summary.valid === 0 && selectedOverrides.size === 0) || isProcessing ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200 dark:shadow-none'}`}
                        >
                            {isProcessing ? <Icons.Loader className="w-4 h-4 animate-spin" /> : <Icons.Check className="w-4 h-4" />}
                            Import {summary.valid + selectedOverrides.size} Records
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CSVImportModal;